import React, { useState, useEffect } from 'react';

import { Box, Typography, CircularProgress } from '@mui/material';

import { useSmsConversations } from '../hooks';
import { StartConversationButton } from '../start-conversation-modal';
import { SmsConversationType } from '../types';
import SmsConversationItem from './SmsConversationItem';
import SmsSearchInput from './SmsSearchInput';

interface SmsSidebarProps {
  selectedConversation: SmsConversationType | null;
  onSelectConversation: (conversation: SmsConversationType) => void;
}

const SmsSidebar: React.FC<SmsSidebarProps> = ({
  selectedConversation,
  onSelectConversation,
}) => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const { conversations, loading } = useSmsConversations(
    debouncedSearch || undefined,
  );

  // Show loading only on initial load (no search, no cached data)
  const isInitialLoading =
    loading && conversations.length === 0 && !debouncedSearch;

  return (
    <Box
      width={320}
      bgcolor="#fff"
      display="flex"
      flexDirection="column"
      p={2}
      borderRadius={2}
    >
      <SmsSearchInput
        value={searchText}
        onChange={setSearchText}
        loading={loading}
      />

      <StartConversationButton onSelectTalent={onSelectConversation} />

      <Box flex={1} overflow="auto">
        {isInitialLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress size={24} />
          </Box>
        ) : conversations.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            py={4}
          >
            <Typography color="text.secondary">
              {debouncedSearch
                ? 'No conversations found'
                : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          conversations.map((conversation) => (
            <SmsConversationItem
              key={conversation.talent.id}
              conversation={conversation}
              isSelected={
                selectedConversation?.talent?.id === conversation.talent?.id
              }
              onClick={() => onSelectConversation(conversation)}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default SmsSidebar;
